import { useQuery } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";

export interface CompanyType {
  id: string;
  label: string;
  description: string;
}

export interface TaxType {
  id: string;
  label: string;
}

export interface HealthcareCompanyMetaResponse {
  types: CompanyType[];
  taxTypes: TaxType[];
  invoiceLanguages: string[];
}

type UseHealthcareCompanyMetaResult = {
  companyTypes: CompanyType[];
  taxTypes: TaxType[];
  invoiceLanguages: string[];
  isLoading: boolean;
  error: Error | null;
};

/**
 * Hook to fetch healthcare company metadata (types, tax types, invoice languages).
 * Metadata is cached for 24 hours since it doesn't change often.
 * 
 * Note: This is in-memory cache (per session). Data is cleared on page refresh.
 */
export const useHealthcareCompanyMeta = (): UseHealthcareCompanyMetaResult => {
  const { GET } = useApiRequest();

  const { data, isLoading, error } = useQuery<HealthcareCompanyMetaResponse>({
    queryKey: ["healthcare-companies-meta"],
    queryFn: () => GET("/api/healthcare-companies/meta"),
    staleTime: 1000 * 60 * 24, // Cache for 24 hours since this data doesn't change often
  });

  return {
    companyTypes: data?.types || [],
    taxTypes: data?.taxTypes || [],
    invoiceLanguages: data?.invoiceLanguages || [],
    isLoading,
    error: error ?? null,
  } satisfies UseHealthcareCompanyMetaResult;
};
